/**
 * StationMetadataDashboard Component
 *
 * This component serves as the main dashboard for displaying and managing station metadata.
 * It provides a navigation drawer with different sections for viewing various types of station information:
 * - Station Info: Basic station details and configuration
 * - Logger Info: Information about the data logging equipment
 * - Equipment Info: Details about installed equipment
 * - Visit Info: Historical visit records and maintenance information
 *
 * The component uses React Router for navigation between different metadata sections and
 * Material-UI for the user interface components.
 */

import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Toolbar,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ComputerIcon from "@mui/icons-material/Computer";
import BuildIcon from "@mui/icons-material/Build";
import EventIcon from "@mui/icons-material/Event";
import StationInfo from "./StationInfo/StationInfo";
import LoggerInfo from "./LoggerInfo/LoggerInfo";
import EquipmentInfo from "./EquipmentInfo/EquipmentInfo";
import VisitInfo from "./VisitInfo/VisitInfo";

// Width of the navigation drawer in pixels
const drawerWidth = 240;

const StationMetadataDashboard = () => {
  // Get the station UID from the URL parameters
  const { uid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Determines the initial selected option based on the current URL path
   * @returns {string} The default section to display (station, logger, equipment, or visit)
   */
  const getInitialSelectedOption = () => {
    const path = location.pathname;
    const matches = path.match(/\/metadata\/(\w+)/);
    return matches ? matches[1] : "station";
  };

  // State to track which section is currently selected
  const [selectedOption, setSelectedOption] = useState(
    getInitialSelectedOption
  );

  /**
   * Updates the selected option whenever the URL path changes
   * This ensures the UI stays in sync with the browser's navigation
   */
  useEffect(() => {
    const currentOption = getInitialSelectedOption();
    setSelectedOption(currentOption);
  }, [location.pathname]);

  /**
   * Handles navigation when a menu item is clicked
   * @param {string} option - The selected menu option (station, logger, equipment, or visit)
   */
  const handleOptionClick = (option) => {
    setSelectedOption(option);
    navigate(`/${uid}/metadata/${option}`);
  };

  // Menu items configuration for the navigation drawer
  const menuItems = [
    { icon: <InfoIcon />, text: "Station Info" },
    { icon: <ComputerIcon />, text: "Logger Info" },
    { icon: <BuildIcon />, text: "Equipment Info" },
    { icon: <EventIcon />, text: "Visit Info" },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      {/* Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1000,
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar /> {/* Spacer to account for the AppBar */}
        <Box sx={{ overflow: "auto" }}>
          <List>
            {menuItems.map((item, index) => (
              <ListItem
                component="div"
                key={index}
                onClick={() =>
                  handleOptionClick(item.text.toLowerCase().split(" ")[0])
                }
                sx={{
                  position: "relative",
                  py: 1.5,
                  px: 3,
                  borderBottom: "none",
                  cursor: "pointer",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    bottom: 0,
                    transform: "translateX(-50%)",
                    width: "80%",
                    borderBottom: "1px solid #e0e0e0",
                  },
                  backgroundColor:
                    selectedOption === item.text.toLowerCase().split(" ")[0]
                      ? "transparent"
                      : "inherit",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: "transparent",
                  },
                  ...(selectedOption ===
                    item.text.toLowerCase().split(" ")[0] && {
                    borderRight: "4px solid black",
                  }),
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      selectedOption === item.text.toLowerCase().split(" ")[0]
                        ? "black"
                        : "inherit",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    color:
                      selectedOption === item.text.toLowerCase().split(" ")[0]
                        ? "black"
                        : "inherit",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {/* Route Configuration */}
        <Routes>
          <Route path="station" element={<StationInfo />} />
          <Route path="logger" element={<LoggerInfo />} />
          <Route path="equipment" element={<EquipmentInfo />} />
          <Route path="visit" element={<VisitInfo />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default StationMetadataDashboard;
